import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductById } from './productData';
import { INITIAL_SWATCH_PRELOAD_COUNT } from '../../utils/pageCriticalImages';
import '../../styles/products/ProductPage.css';

function ProductPage() {
  const { productId } = useParams();
  const product = getProductById(productId);
  const [activeTab, setActiveTab] = useState('swatches');
  const isCompactBlindSummary = ['aluminum-blind', 'vertical-blind', 'wood-blind', 'faux-blind', 'plantation-shutter', 'eclipse-shutter', 'honeycomb-shade', 'sheer-shade', 'revolve-shade', 'roller-shade', 'outdoor-shade'].includes(product?.id);
  const isPlantationShutter = product?.id === 'plantation-shutter';
  const specialityShapes = product?.specialityShapes;

  // Remove metadata-like entries such as "Best For:" from swatch cards.
  const displaySwatches = useMemo(() => {
    if (!product?.swatches?.length) return [];
    return product.swatches.filter((swatch) => {
      const cardLabel = `${swatch?.colorName || swatch?.name || ''}`.trim().toLowerCase();
      const excludedPrefixes = ['best for', 'key features', 'style options'];
      return !excludedPrefixes.some((prefix) => cardLabel.startsWith(prefix));
    });
  }, [product]);

  const sampleDescription = useMemo(() => {
    const name = product?.name || 'Product';
    const singular = name.endsWith('s') ? name.slice(0, -1) : name;
    return `${singular} Sample`;
  }, [product]);

  const tiltOptionsImage = product?.tiltOptionsImage;
  const specialityOptionsImage = product?.specialityOptionsImage;
  const specialityOptions = product?.specialityOptions || [];
  const motorizationImage = product?.motorizationImage;
  const motorization = product?.motorization || [];

  if (!product) {
    return (
      <section className="product-page">
        <div className="container">
          <div className="error-message">
            <h2>Product not found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/" className="btn btn-primary">Back to Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="product-page">
      <div className="container">
        <div className="product-shell">
          <div className="product-header">
            <h1>{product.name}</h1>
          </div>

          <div className="product-top-row">
            <div className="product-media-column">
              <div className="main-image">
                {product.images?.[0] && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>

            <div className="product-info-column">
              <p className="description">{product.description}</p>
              <p className="long-description">{product.longDescription}</p>

              {product.bestFor && (
                <p className="best-for">
                  <strong>Best For:</strong> {product.bestFor}
                </p>
              )}
            </div>
          </div>

          <hr className="content-divider" />

          <div className="lookup-tabs" role="tablist" aria-label="Product lookup tabs">
            <button
              type="button"
              className={`lookup-btn ${activeTab === 'swatches' ? 'active' : ''}`}
              onClick={() => setActiveTab('swatches')}
            >
              Samples
            </button>
            <button
              type="button"
              className={`lookup-btn ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
            >
              Features & Styles
            </button>
            {tiltOptionsImage && (
              <button
                type="button"
                className={`lookup-btn ${activeTab === 'tilt-options' ? 'active' : ''}`}
                onClick={() => setActiveTab('tilt-options')}
              >
                Tilt Options
              </button>
            )}
            {isPlantationShutter && (
              <button
                type="button"
                className={`lookup-btn ${activeTab === 'speciality-options' ? 'active' : ''}`}
                onClick={() => setActiveTab('speciality-options')}
              >
                Speciality Options
              </button>
            )}
            {isPlantationShutter && (
              <button
                type="button"
                className={`lookup-btn ${activeTab === 'motorization' ? 'active' : ''}`}
                onClick={() => setActiveTab('motorization')}
              >
                Motorization
              </button>
            )}
            {specialityShapes && (
              <button
                type="button"
                className={`lookup-btn ${activeTab === 'speciality-shapes' ? 'active' : ''}`}
                onClick={() => setActiveTab('speciality-shapes')}
              >
                Speciality Shapes
              </button>
            )}
          </div>

          {/* ── Features Tab ── */}
          {activeTab === 'features' && (
            <div
              className={`features-checklist ${isCompactBlindSummary ? 'features-checklist-compact' : ''}`}
              role="tabpanel"
              aria-label="Features and styles"
            >
              {isCompactBlindSummary ? (
                <div className="aluminum-summary-grid">
                  <article className="aluminum-summary-card">
                    <h3>Key Features</h3>
                    <ul className="features-list-simple compact-list">
                      {(product.keyFeatures || []).map((feature, index) => (
                        <li key={`${feature}-${index}`}>{feature}</li>
                      ))}
                    </ul>
                  </article>
                  <article className="aluminum-summary-card">
                    <h3>Style Options</h3>
                    <ul className="features-list-simple compact-list">
                      {(product.styleOptions || []).map((option, index) => (
                        <li key={`${option}-${index}`}>{option}</li>
                      ))}
                    </ul>
                  </article>
                </div>
              ) : (
                <ul className="features-list-simple">
                  {(product.features || []).map((feature, index) => (
                    <li key={`${feature}-${index}`}>{feature}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ── Tilt Options Tab ── */}
          {activeTab === 'tilt-options' && tiltOptionsImage && (
            <div className="speciality-shapes-panel" role="tabpanel" aria-label="Tilt options">
              <div className="speciality-shapes-image-wrap">
                <img
                  src={tiltOptionsImage.imageUrl}
                  alt={tiltOptionsImage.imageAlt || 'Tilt options'}
                  className="speciality-shapes-image"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* ── Speciality Options Tab ── */}
          {activeTab === 'speciality-options' && isPlantationShutter && (
            <div className="features-checklist features-checklist-compact" role="tabpanel" aria-label="Speciality options">
              {specialityOptions.length > 0 ? (
                <>
                  <ul className="features-list-simple compact-list">
                    {specialityOptions.map((option, index) => {
                      const optionTitle = typeof option === 'string' ? '' : option?.title || '';
                      const optionDescription = typeof option === 'string' ? option : option?.description || '';
                      return (
                        <li key={`${optionTitle || optionDescription}-${index}`}>
                          {optionTitle ? <><strong>{optionTitle}:</strong> {optionDescription}</> : optionDescription}
                        </li>
                      );
                    })}
                  </ul>
                  {specialityOptionsImage?.imageUrl && (
                    <div className="speciality-shapes-panel" role="presentation">
                      <div className="speciality-shapes-image-wrap">
                        <img
                          src={specialityOptionsImage.imageUrl}
                          alt={specialityOptionsImage.imageAlt || 'Speciality options'}
                          className="speciality-shapes-image"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="no-swatches">Speciality options coming soon.</p>
              )}
            </div>
          )}

          {/* ── Motorization Tab ── */}
          {activeTab === 'motorization' && isPlantationShutter && (
            <div className="features-checklist features-checklist-compact" role="tabpanel" aria-label="Motorization">
              {motorization.length > 0 ? (
                <>
                  {motorization.map((section, sectionIndex) => {
                    const sectionTitle = typeof section === 'string' ? '' : section?.title || '';
                    const sectionDetails = Array.isArray(section?.details)
                      ? section.details
                      : [typeof section === 'string' ? section : ''];

                    return (
                      <div key={`${sectionTitle || 'motorization'}-${sectionIndex}`} className="motorization-section">
                        {sectionTitle && <h3 className="motorization-title">{sectionTitle}</h3>}
                        <ul className="features-list-simple compact-list">
                          {sectionDetails.map((detail, detailIndex) => (
                            <li key={`${sectionTitle || 'motorization-detail'}-${detailIndex}`}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                  {motorizationImage?.imageUrl && (
                    <div className="speciality-shapes-panel" role="presentation">
                      <div className="speciality-shapes-image-wrap">
                        <img
                          src={motorizationImage.imageUrl}
                          alt={motorizationImage.imageAlt || 'Motorization options'}
                          className="speciality-shapes-image"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="no-swatches">Motorization options coming soon.</p>
              )}
            </div>
          )}

          {/* ── Speciality Shapes Tab ── */}
          {activeTab === 'speciality-shapes' && specialityShapes && (
            <div className="speciality-shapes-panel" role="tabpanel" aria-label="Speciality shapes">
              <div className="speciality-shapes-image-wrap">
                <img
                  src={specialityShapes.imageUrl}
                  alt={specialityShapes.imageAlt}
                  className="speciality-shapes-image"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* ── Swatches Tab ── */}
          {activeTab === 'swatches' && (
            <div role="tabpanel" aria-label="Swatches and samples">
              {(() => {
                const colorSwatches = displaySwatches.filter((s) => s.collection !== 'Hinge');
                const hingeSwatches = displaySwatches.filter((s) => s.collection === 'Hinge');

                return (
                  <>
                    {colorSwatches.length > 0 ? (
                      <div className="lookup-grid swatch-grid">
                        {colorSwatches.map((swatch, index) => (
                          <article key={swatch.id ?? swatch.name} className="lookup-card swatch-card">
                            <div className="swatch-media">
                              {swatch.imageUrl ? (
                                <img
                                  src={swatch.imageUrl}
                                  alt={swatch.colorName}
                                  loading={index < INITIAL_SWATCH_PRELOAD_COUNT ? 'eager' : 'lazy'}
                                  decoding="async"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="swatch-image-placeholder" aria-hidden="true" />
                              )}
                              <div className="swatch-overlay">
                                <h3>{swatch.colorName}</h3>
                                <p>{sampleDescription}</p>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="no-swatches">Swatches coming soon.</p>
                    )}

                    {hingeSwatches.length > 0 && (
                      <>
                        <hr className="content-divider" />
                        <h3 className="swatch-section-heading">Hinge Options</h3>
                        <div className="lookup-grid swatch-grid">
                          {hingeSwatches.map((swatch) => (
                            <article key={swatch.id ?? swatch.name} className="lookup-card swatch-card">
                              <div className="swatch-media">
                                {swatch.imageUrl ? (
                                  <img
                                    src={swatch.imageUrl}
                                    alt={swatch.colorName}
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="swatch-image-placeholder" aria-hidden="true" />
                                )}
                                <div className="swatch-overlay">
                                  <h3>{swatch.colorName}</h3>
                                  <p>Hinge Option</p>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ProductPage;
